import { useEffect, useState, MutableRefObject } from 'react';
import { api } from '../api/client';

interface PersistenceRefs {
  tabAwayMsRef: MutableRefObject<number>;
  phoneMsRef: MutableRefObject<number>;
  awayMsRef: MutableRefObject<number>;
  notFocusedMsRef: MutableRefObject<number>;
  drowsyMsRef: MutableRefObject<number>;
  postureMsRef: MutableRefObject<number>;
  sustainedFocusMsRef: MutableRefObject<number>;
  totalFocusMsRef: MutableRefObject<number>;
  focusHistoryRef: MutableRefObject<any[]>;
  closedTotalMsRef?: MutableRefObject<number>;
  hiddenTotalMsRef?: MutableRefObject<number>;
  yawnPenaltyMsRef?: MutableRefObject<number>;
}

export const useCameraPersistence = (sessionId: string | undefined, refs: PersistenceRefs) => {
  const [isRestored, setIsRestored] = useState(false);

  useEffect(() => {
    if (!sessionId) {
      setIsRestored(false);
      return;
    }
    
    const restore = async () => {
        const closedTotalRef = refs.closedTotalMsRef || refs.hiddenTotalMsRef;
        const yawnPenaltyRef = refs.yawnPenaltyMsRef;

        let serverData: any = null;
        let localData: any = null;

        // 1. Fetch Server Data
        try {
        const res = await api.get(`/session/${sessionId}/metrics`, { skipAuthRedirect: true } as any);
        if (res.data && res.data.metrics) {
                serverData = res.data;
            }
        } catch (err) {
            // Server fetch failed
        }

        // 2. Read Local Data
        const key = `sessionMonitor:${sessionId}`;
        try {
            const stored = typeof window !== 'undefined' ? window.localStorage.getItem(key) : null;
            if (stored) {
                localData = JSON.parse(stored);
            }
        } catch {}

        // 3. Determine Source
        let source = 'none';
        let dataToUse = null;
        
        const serverTs = serverData?.updatedAt ? new Date(serverData.updatedAt).getTime() : 0;
        const localTs = localData?.ts || 0;

        if (serverData && (!localData || serverTs >= localTs)) {
            source = 'server';
            dataToUse = serverData;
        } else if (localData) {
            source = 'local';
            dataToUse = localData;
        }

        if (source === 'server') {
             const { metrics, history } = dataToUse;
             refs.phoneMsRef.current = metrics.phoneMs || 0;
             refs.awayMsRef.current = metrics.awayMs || 0;
             refs.tabAwayMsRef.current = metrics.tabAwayMs || 0;
             // Support both new and legacy names
             if (closedTotalRef) closedTotalRef.current = metrics.closedTotalMs || metrics.hiddenTotalMs || 0;
             refs.notFocusedMsRef.current = metrics.notFocusedMs || 0;
             refs.drowsyMsRef.current = metrics.drowsyMs || 0;
             refs.postureMsRef.current = metrics.postureMs || 0;
             if (yawnPenaltyRef) yawnPenaltyRef.current = metrics.yawnPenaltyMs || 0;
             refs.totalFocusMsRef.current = metrics.totalFocusMs || 0;
             
             if (Array.isArray(history)) {
                 refs.focusHistoryRef.current = history;
             }
             
             // Gap logic for server
             let lastTs = 0;
             if (history && history.length > 0) {
                 lastTs = history[history.length - 1].timestamp;
             } else if (dataToUse.startTime) {
                 lastTs = dataToUse.startTime;
             }

             // Restore start time to localStorage if available (fixes score reset on refresh)
             if (dataToUse.startTime) {
                 try {
                     const st = dataToUse.startTime;
                     const key = `sessionTimes:${sessionId}`;
                     const existing = localStorage.getItem(key);
                     const t = existing ? JSON.parse(existing) : {};
                     if (!t.startMs || t.startMs !== st) {
                         t.startMs = st;
                         localStorage.setItem(key, JSON.stringify(t));
                         if (typeof window !== 'undefined') (window as any).__sessionStartMs = st;
                     }
                 } catch (e) {
            // Failed to restore start time
        }
             }
             
             const now = Date.now();
             const gap = now - lastTs;
             if (lastTs > 0 && gap > 5000 && gap < 86400000) {
                 const secondsAway = Math.floor(gap / 1000);
                if (secondsAway > 65) {
                    if (closedTotalRef) closedTotalRef.current += gap;
                    console.log('[Persistence] Server restored with PAUSE gap:', secondsAway, 's');
                } else if (secondsAway > 0) {
                    // Short gap < 65s: Treat as focus time (no penalty)
                    refs.totalFocusMsRef.current += gap;
                    console.log('[Persistence] Server restored with FOCUS gap:', secondsAway, 's');
                }
             }
             console.log('[Persistence] Restored from SERVER (ts=' + serverTs + ')');
        } else if (source === 'local') {
             const data = dataToUse;
             refs.tabAwayMsRef.current = data.metrics?.tabAwayMs || 0;
             if (closedTotalRef) closedTotalRef.current = data.metrics?.closedTotalMs || data.metrics?.hiddenTotalMs || 0;
             if (data.metrics) {
                 refs.phoneMsRef.current = data.metrics.phoneMs || 0;
                 refs.awayMsRef.current = data.metrics.awayMs || 0;
                 refs.notFocusedMsRef.current = data.metrics.notFocusedMs || 0;
                 refs.drowsyMsRef.current = data.metrics.drowsyMs || 0;
                 refs.postureMsRef.current = data.metrics.postureMs || 0;
                 if (yawnPenaltyRef) yawnPenaltyRef.current = data.metrics.yawnPenaltyMs || 0;
                 refs.sustainedFocusMsRef.current = data.metrics.sustainedFocusMs || 0;
                 refs.totalFocusMsRef.current = data.metrics.totalFocusMs || 0;
             }
             if (Array.isArray(data.history)) {
                 refs.focusHistoryRef.current = data.history;
             }
             
             const lastTs = data.ts || 0;
             const now = Date.now();
             const gap = now - lastTs;
             if (lastTs > 0 && gap > 5000 && gap < 86400000) {
                 const secondsAway = Math.floor(gap / 1000);
                if (secondsAway > 65) {
                    if (closedTotalRef) closedTotalRef.current += gap;
                } else {
                    // Short gap < 65s: Treat as focus time (no penalty)
                    refs.totalFocusMsRef.current += gap;
                }
             }
             console.log('[Persistence] Restored from LOCAL (ts=' + localTs + ')');
        } else {
             // Fallback to sessionStats if neither found
             try {
                 const statsRaw = typeof window !== 'undefined' ? window.localStorage.getItem(`sessionStats:${sessionId}`) : null;
                 if (statsRaw) {
                     const stats = JSON.parse(statsRaw);
                     const uid = typeof window !== 'undefined' ? window.localStorage.getItem('userId') : null;
                     const sameUser = !uid || !stats?.userId || String(stats.userId) === String(uid);
                     if (stats && sameUser) {
                         refs.phoneMsRef.current = (stats.phoneSeconds || 0) * 1000;
                         refs.awayMsRef.current = (stats.awaySeconds || 0) * 1000;
                         refs.tabAwayMsRef.current = (stats.tabAwaySeconds || 0) * 1000;
                         if (closedTotalRef) closedTotalRef.current = (stats.hiddenSeconds || 0) * 1000;
                         refs.notFocusedMsRef.current = (stats.notFocusedSeconds || 0) * 1000;
                         refs.drowsyMsRef.current = (stats.drowsySeconds || 0) * 1000;
                         refs.postureMsRef.current = (stats.postureSeconds || 0) * 1000;
                         if (Array.isArray(stats.focusHistory)) {
                             refs.focusHistoryRef.current = stats.focusHistory;
                         }
                     }
                     console.log('[Persistence] Restored from LEGACY sessionStats');
                 }
             } catch {}
        }
        
        setIsRestored(true);
    };

    restore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return isRestored;
};
